import React, { useCallback, useMemo } from "react";
import { TypeDropdown } from "@adsk/acc-issues-common/components/input/TypeDropdown";
import { TypeDisplayWithHydration } from "@adsk/acc-issues-common/components/input/TypeDropdown/TypeDisplayWithHydration";
import { CompactEditableFieldWrapper } from "@adsk/acc-issues-common/components/primitive/EditableFieldWrapper";
import { IssueStatus } from "@adsk/acc-issues-types";
import FormField from "@adsk/alloy-react-form-field";
import { css } from "styled-components";
import { useIsTypeExists } from "@adsk/acc-issues-common/components/input/TypeDropdown/useIsTypeExists";
import { useFieldTestId } from "../IssueFieldPresentationContext";

export interface TypeFieldInterface {
  status?: IssueStatus;
  typeId?: string;
  typeCategoryId?: string;
  onChange: (arg0: {
    typeId: string;
    inlineEditOnChange: (arg0: string | undefined) => void;
  }) => void;
  label: string;
  containerRef?: React.RefObject<HTMLElement | null>;
  disabled?: boolean;
  variant?: string;
  showDisabledReason?: boolean;
}

const HORIZONTAL_VARIATION_STYLES = css`
  display: flex;
  padding-top: 0;
  label {
    flex: none;
  }
`;

export const TypeField = ({
  status,
  typeId,
  typeCategoryId,
  onChange,
  label,
  containerRef,
  disabled,
  showDisabledReason = true,
  variant = "vertical",
}: TypeFieldInterface) => {
  const testId = useFieldTestId("type-field");

  const renderValue = useCallback(
    () => (
      <TypeDisplayWithHydration
        typeId={typeId}
        typeCategoryId={typeCategoryId}
        status={status}
      />
    ),
    [typeId, typeCategoryId, status]
  );

  const isTypeExists = useIsTypeExists({ typeId, typeCategoryId });
  const typeExists = useMemo(() => isTypeExists(), [isTypeExists]);

  const renderInput = useCallback(
    ({ value, onChange: inlineEditOnChange }) => (
      <TypeDropdown
        selectedTypeId={typeId || ""}
        onChange={typeId => onChange({ typeId, inlineEditOnChange })}
        defaultIsOpen={true}
        onClose={() => inlineEditOnChange(value)}
        issueStatus={status}
      />
    ),

    [onChange, typeId, status]
  );

  return (
    <CompactEditableFieldWrapper
      testId={testId}
      fieldKey="issueTypeId"
      label={label}
      labelVariant={
        variant === "vertical"
          ? FormField.LABEL_VARIANTS.TOP
          : FormField.LABEL_VARIANTS.SIDE
      }
      fieldWrapperStyle={
        variant === "horizontal" ? HORIZONTAL_VARIATION_STYLES : undefined
      }
      renderValue={renderValue}
      renderInput={renderInput}
      value={typeExists ? typeId : undefined}
      disabled={disabled}
      containerRef={containerRef}
      showDisabledReason={showDisabledReason}
    />
  );
};
